import { useDispatch, useSelector } from "react-redux";

import { useTranslation } from "hooks";
import Modal from "./Modal";
import ModalDetails from "./ModalDetails";
import ModalLeaveApp from "./ModalLeaveApp";
import ModalReview from "./ModalReview";
import ModalSave from "./ModalSave";
import ModalShare from "./ModalShare";
import ModalInfo from "./ModalInfo";
import ModalBladePatternCompare from "./ModalBladePatternCompare";
import ModalVideo from "./ModalVideo";
import ModalSelectGoalGear from "./ModalSelectGoalGear";
import ModalSelectProCustom from "./ModalSelectProCustom";
import ModalCustomisationsComplete from "./ModalCustomisationsComplete";
import { selectProductExpectedDelivery, selectProductCost } from "store/app";
import { closeModal } from "store/app/appSlice";

export default function ModalSelector({ appLogo, product, modals }) {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const dispatchCloseModal =
    ({ modalKey }) =>
    () => {
      dispatch(closeModal({ modalKey }));
    };

  const expectedDelivery = useSelector(selectProductExpectedDelivery);
  const cost = useSelector(selectProductCost);

  return modals.map(({ key, modal, modalProps }, index) => {
    const commonModalProps = {
      key,
      isTopMostModal: index + 1 === modals.length,
      onClose: dispatchCloseModal({ modalKey: key }),
    };

    switch (modal) {
      case "ReviewModal":
        return (
          <ModalReview
            {...commonModalProps}
            appLogo={appLogo}
            title={`${t("Your")} ${product.subName || product.name} ${t(
              "Specs"
            )}`}
            gameFitDealerLink={product?.gameFitDealerLink}
            cost={cost}
            expectedDelivery={expectedDelivery}
            closeButtonLabel="CLOSE REVIEW"
          />
        );

      case "SaveModal":
        const saveYourCustomDesignObj =
          product?.saveYourCustomDesign?.content || {};
        return (
          <ModalSave
            {...commonModalProps}
            title={saveYourCustomDesignObj.title}
            saveYourCustomDesignObj={saveYourCustomDesignObj}
            {...modalProps}
          />
        );

      case "ShareModal":
        const shareYourCustomDesignObj =
          product?.shareYourCustomDesign?.content || {};
        return (
          <ModalShare
            {...commonModalProps}
            title={shareYourCustomDesignObj.title}
            shareYourCustomDesignObj={shareYourCustomDesignObj}
            {...modalProps}
          />
        );

      case "CustomisationsCompleteModal":
        return (
          <ModalCustomisationsComplete {...commonModalProps} {...modalProps} />
        );

      case "InfoModal":
        return <ModalInfo {...commonModalProps} {...modalProps} />;

      case "VideoModal":
        return <ModalVideo {...commonModalProps} {...modalProps} />;

      case "CompareModal":
        return (
          <ModalBladePatternCompare
            {...commonModalProps}
            appLogo={appLogo}
            closeButtonLabel="CLOSE DETAILS"
            {...modalProps}
          />
        );

      case "DetailsModal":
        return (
          <ModalDetails
            {...commonModalProps}
            appLogo={appLogo}
            closeButtonLabel="CLOSE DETAILS"
            {...modalProps}
          />
        );

      case "LeaveAppModal":
        const leaveYourCustomDesignObj =
          product?.leaveYourCustomDesign?.content || {};
        return (
          <ModalLeaveApp
            {...commonModalProps}
            title={leaveYourCustomDesignObj.title}
            leaveYourCustomDesignObj={leaveYourCustomDesignObj}
            {...modalProps}
          />
        );

      case "SelectGoalGearModal":
        return <ModalSelectGoalGear {...commonModalProps} {...modalProps} />;

      case "SelectProCustomModal":
        const proCustomGraphicSelection =
          product?.proCustomGraphicSelection?.content || {};
        return (
          <ModalSelectProCustom
            {...commonModalProps}
            title={proCustomGraphicSelection.title}
            text={proCustomGraphicSelection.text}
            description={proCustomGraphicSelection.description}
            {...modalProps}
          />
        );

      case "Modal":
        return (
          <Modal
            {...commonModalProps}
            appLogo={appLogo}
            closeButtonLabel="CLOSE DETAILS"
            {...modalProps}
          />
        );

      default:
        return null;
    }
  });
}
